import { useApolloClient } from '@apollo/client'
import { CircleCheck, Loader, MoreHorizontal } from 'lucide-react'
import { useState, useTransition } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import {
  RoleID,
  canCompanyDeveloperManageCapabilities,
  isCompanyDeveloper,
} from '@/lib/rbac'
import type {
  InvitationLinkQuery,
  InvitationLinkQueryVariables,
} from '@/routes/user-management/users-table/cells/actions-cell.types.gen'
import type { Row } from '@/routes/user-management/users-table/types'
import type { CellContext } from '@tanstack/react-table'

const INVITATION_LINK = gql`
  query InvitationLink($id: String!) {
    invitationLink(idInvitation: $id)
  }
`

export const ActionsCell = ({ row }: CellContext<Row, unknown>) => {
  const client = useApolloClient()
  const { rbac, rolesInCompany } = useRBAC()
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const [isCopied, setIsCopied] = useState(false)
  const [isInvitationLinkPending, startInvitationLinkTransition] =
    useTransition()
  const isInvitation = !row.original?.user?.firstName
  const isCompanyDeveloperViewer = isCompanyDeveloper(rolesInCompany)
  const canCompanyDeveloperManageTarget = canCompanyDeveloperManageCapabilities(
    row.original.capabilities ?? []
  )
  const canManageTarget =
    rbac.company(idCompany).users.update.can() ||
    (isCompanyDeveloperViewer && canCompanyDeveloperManageTarget)
  const isOwner = rbac.company(idCompany).users.ownership.update.can()
  const canDelete =
    rbac.kinsta().superadmin.can() ||
    (isCompanyDeveloperViewer && canCompanyDeveloperManageTarget) ||
    rolesInCompany.some(
      (role) => role === RoleID.COMPANY_OWNER || role === RoleID.COMPANY_ADMIN
    )
  const canTransferOwnership =
    Boolean(
      row.original.capabilities.find((c) => c.idRole === RoleID.COMPANY_OWNER)
    ) && isOwner
  const canUpdate = !isInvitation && canManageTarget
  const canCopyInvitationLink = isInvitation && canManageTarget
  const canCancelInvitation = isInvitation && canManageTarget
  const canDeleteUser = !isInvitation && canDelete

  if (
    !canTransferOwnership &&
    !canUpdate &&
    !canCopyInvitationLink &&
    !canCancelInvitation &&
    !canDeleteUser
  ) {
    return null
  }

  return (
    <div className="text-right mr-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canTransferOwnership ? (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() =>
                row.original.id &&
                navigateToPath({
                  pathname: '/user-management/transfer-ownership',
                })
              }
            >
              Transfer ownership
            </DropdownMenuItem>
          ) : null}
          {canUpdate &&
          !row.original.capabilities.find(
            (c) => c.idRole === RoleID.COMPANY_OWNER
          ) ? (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() =>
                row.original.id &&
                navigateToPath({
                  pathname: '/user-management/update-user-access/:idUser',
                  variables: { idUser: row.original.user.id },
                  options: { replace: true },
                })
              }
            >
              Update
            </DropdownMenuItem>
          ) : null}
          {canCopyInvitationLink ? (
            <DropdownMenuItem
              className="cursor-pointer"
              disabled={isInvitationLinkPending}
              onClick={(e) => {
                e.preventDefault()
                startInvitationLinkTransition(async () => {
                  if (!row.original.id) {
                    return
                  }
                  const data = await client.query<
                    InvitationLinkQuery,
                    InvitationLinkQueryVariables
                  >({
                    query: INVITATION_LINK,
                    variables: { id: row.original.id },
                  })
                  navigator.clipboard
                    .writeText(data?.data?.invitationLink ?? '')
                    .then(
                      () => {
                        setIsCopied(true)
                        setTimeout(() => setIsCopied(false), 2000)
                      },
                      () => setIsCopied(false)
                    )
                })
              }}
            >
              {isInvitationLinkPending ? (
                <Loader className="animate-spin" />
              ) : null}
              {isCopied ? (
                <div className="flex gap-1 items-center">
                  <CircleCheck className="size-4 fill-emerald-600 stroke-background" />
                  Copied
                </div>
              ) : (
                'Copy invitation link'
              )}
            </DropdownMenuItem>
          ) : null}
          {canCancelInvitation ? (
            <DropdownMenuItem
              className="cursor-pointer text-destructive"
              onClick={() =>
                row.original.id &&
                navigateToPath({
                  pathname: '/user-management/cancel-invitation/:idInvitation',
                  variables: { idInvitation: row.original.id },
                  options: { replace: true },
                })
              }
            >
              Cancel invitation
            </DropdownMenuItem>
          ) : canDeleteUser ? (
            <>
              <DropdownMenuItem
                className="cursor-pointer text-destructive"
                disabled={
                  !isOwner &&
                  Boolean(
                    row.original.capabilities.find(
                      (c) => c.idRole === 'company-owner'
                    )
                  )
                }
                onClick={() =>
                  row.original.id &&
                  navigateToPath({
                    pathname: '/user-management/remove-user/:idUser',
                    variables: { idUser: row.original.user.id },
                    options: { replace: true },
                  })
                }
              >
                Delete
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
